import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, onSnapshot, query, where, orderBy } from 'firebase/firestore';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, Legend
} from 'recharts';

const COLORS = ['#1A1A2E','#C9A84C','#2E7D52','#C0392B','#7C3AED','#185FA5','#F59E0B','#9B8F7A'];
const TT = { contentStyle:{ background:'#FFFFFF', border:'1px solid #E2DDD6', color:'#1A1A2E', fontSize:12, borderRadius:8 } };

const S = {
  page:  { padding:'0 24px 24px', color:'#1A1A2E' },
  card:  { background:'#FFFFFF', border:'1px solid #E2DDD6', borderRadius:12, padding:20 },
  title: { fontSize:11, color:'#9B8F7A', textTransform:'uppercase', letterSpacing:1.5, fontWeight:700, marginBottom:16 },
  grid2: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))', gap:16, marginBottom:16 },
  grid4: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:12, marginBottom:20 },
  stat:  (c) => ({ fontSize:32, fontWeight:800, color:c, lineHeight:1 }),
};

export default function AnalyticsPage({ incidents=[], patrols=[], users=[] }) {
  const [shifts, setShifts]     = useState([]);
  const [visitors, setVisitors] = useState([]);
  const [tab, setTab]           = useState('overview');
  const tenantId = 'osecurity-ltd';

  useEffect(() => {
    const q = query(collection(db,'shifts'), where('tenant_id','==',tenantId));
    return onSnapshot(q, snap => setShifts(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  useEffect(() => {
    const q = query(collection(db,'visitors'), where('tenant_id','==',tenantId));
    return onSnapshot(q, snap => setVisitors(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, []);

  // ── COMPUTED STATS ──────────────────────────────────────────
  const totalIncidents   = incidents.length;
  const openIncidents    = incidents.filter(i=>i.status==='open').length;
  const criticalCount    = incidents.filter(i=>(i.escalationLevel||'').includes('3')).length;
  const patrolRate       = patrols.length>0 ? Math.round(patrols.filter(p=>p.completed).length/patrols.length*100) : 0;

  // ── INCIDENTS BY CATEGORY ───────────────────────────────────
  const categoryData = Object.entries(
    incidents.reduce((acc,i)=>{ const c=i.category||'Unknown'; acc[c]=(acc[c]||0)+1; return acc; },{})
  ).map(([name,value])=>({ name:name.length>14?name.slice(0,14)+'…':name, value }))
   .sort((a,b)=>b.value-a.value).slice(0,8);

  // ── INCIDENTS LAST 7 DAYS ───────────────────────────────────
  const trendData = Array.from({length:7},(_,i)=>{
    const d = new Date(); d.setDate(d.getDate()-(6-i));
    const label = d.toLocaleDateString('en-GB',{day:'2-digit',month:'short'});
    const count = incidents.filter(inc=>{
      try { const id = inc.createdAt?.toDate?.()||new Date(inc.createdAt); return id.toDateString()===d.toDateString(); } catch{return false;}
    }).length;
    return { date:label, incidents:count };
  });

  // ── ESCALATION BREAKDOWN ────────────────────────────────────
  const escalationData = [
    { name:'L1 Minor',    value:incidents.filter(i=>!(i.escalationLevel||'').includes('2')&&!(i.escalationLevel||'').includes('3')).length, color:'#2E7D52' },
    { name:'L2 Serious',  value:incidents.filter(i=>(i.escalationLevel||'').includes('2')).length, color:'#C9A84C' },
    { name:'L3 Emergency',value:criticalCount, color:'#C0392B' },
  ].filter(d=>d.value>0);

  // ── INCIDENTS BY SITE ────────────────────────────────────────
  const siteData = Object.entries(
    incidents.reduce((acc,i)=>{ const s=i.siteName||i.site||'Unknown'; acc[s]=(acc[s]||0)+1; return acc; },{})
  ).map(([name,value])=>({ name:name.length>16?name.slice(0,16)+'…':name, value }))
   .sort((a,b)=>b.value-a.value).slice(0,6);

  // ── SHIFTS BY TYPE ────────────────────────────────────────────
  const shiftTypeData = Object.entries(
    shifts.reduce((acc,s)=>{ const t=s.type||'Day'; acc[t]=(acc[t]||0)+1; return acc; },{})
  ).map(([name,value])=>({ name, value }));

  // ── VISITOR STATS ────────────────────────────────────────────
  const visitorOnSite  = visitors.filter(v=>v.status==='on_site').length;
  const visitorTotal   = visitors.length;
  const visitorToday   = visitors.filter(v=>{ try{ const d=v.expected_arrival?.toDate?.(); return d&&d.toDateString()===new Date().toDateString(); }catch{return false;} }).length;

  // ── RESPONSE TIME (avg minutes between creation & first update) ─
  const avgResponse = incidents.filter(i=>i.status==='closed'&&i.createdAt&&i.updatedAt).reduce((sum,i,_,arr)=>{
    try {
      const s=i.createdAt.toDate?.()??new Date(i.createdAt);
      const e=i.updatedAt.toDate?.()??new Date(i.updatedAt);
      return sum+(e-s)/(1000*60*arr.length);
    } catch{return sum;}
  },0);

  const tabs = ['overview','incidents','patrols','shifts'];

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'20px 0 16px' }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:'#1A1A2E' }}>Analytics</div>
          <div style={{ fontSize:11, color:'#9B8F7A', marginTop:2 }}>Live data · {totalIncidents} incidents · {patrols.length} patrols · {shifts.length} shifts</div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          {tabs.map(t=>(
            <button key={t} onClick={()=>setTab(t)} style={{
              background:tab===t?'#1A1A2E':'#FFFFFF', color:tab===t?'#C9A84C':'#6B6055',
              border:'1px solid #E2DDD6', padding:'6px 14px', borderRadius:7,
              cursor:'pointer', fontSize:12, fontWeight:700, textTransform:'capitalize'
            }}>{t}</button>
          ))}
        </div>
      </div>

      {/* OVERVIEW TAB */}
      {tab==='overview' && (<>
        {/* KPI Stats */}
        <div style={S.grid4}>
          {[
            ['Total Incidents', totalIncidents, '#1A1A2E'],
            ['Open',            openIncidents,  '#C0392B'],
            ['Critical (L3)',   criticalCount,  '#7C3AED'],
            ['Patrol Rate',     patrolRate+'%', '#2E7D52'],
            ['Shifts Scheduled',shifts.length,  '#C9A84C'],
            ['Visitors Today',  visitorToday,   '#185FA5'],
            ['On Site Now',     visitorOnSite,  '#2E7D52'],
            ['Avg Response',    avgResponse>0?Math.round(avgResponse)+'m':'—', '#9B8F7A'],
          ].map(([l,v,c])=>(
            <div key={l} style={{ ...S.card, borderLeft:`3px solid ${c}`, padding:'14px 16px' }}>
              <div style={S.stat(c)}>{v}</div>
              <div style={{ fontSize:10, color:'#9B8F7A', letterSpacing:1.5, textTransform:'uppercase', marginTop:4 }}>{l}</div>
            </div>
          ))}
        </div>

        <div style={S.grid2}>
          {/* Incident trend */}
          <div style={S.card}>
            <div style={S.title}>Incidents — Last 7 Days</div>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trendData}>
                <defs>
                  <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#C9A84C" stopOpacity={0.2}/>
                    <stop offset="95%" stopColor="#C9A84C" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip {...TT}/>
                <Area type="monotone" dataKey="incidents" stroke="#C9A84C" strokeWidth={2} fill="url(#grad)" dot={{ fill:'#C9A84C', r:3 }}/>
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Escalation pie */}
          <div style={S.card}>
            <div style={S.title}>Incidents by Escalation Level</div>
            {escalationData.length===0
              ? <div style={{ textAlign:'center', padding:60, color:'#9B8F7A', fontSize:13 }}>No incident data yet</div>
              : <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie data={escalationData} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({name,value})=>`${name}: ${value}`} labelLine={false}>
                      {escalationData.map((e,i)=><Cell key={i} fill={e.color}/>)}
                    </Pie>
                    <Tooltip {...TT}/>
                    <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize:11 }}/>
                  </PieChart>
                </ResponsiveContainer>
            }
          </div>
        </div>

        <div style={S.grid2}>
          {/* Category bar */}
          <div style={S.card}>
            <div style={S.title}>Incidents by Category</div>
            {categoryData.length===0
              ? <div style={{ textAlign:'center', padding:60, color:'#9B8F7A', fontSize:13 }}>No incidents recorded</div>
              : <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={categoryData} layout="vertical">
                    <XAxis type="number" tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                    <YAxis type="category" dataKey="name" tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} width={100}/>
                    <Tooltip {...TT}/>
                    <Bar dataKey="value" fill="#1A1A2E" radius={[0,4,4,0]}>
                      {categoryData.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
            }
          </div>

          {/* Site bar */}
          <div style={S.card}>
            <div style={S.title}>Incidents by Site</div>
            {siteData.length===0
              ? <div style={{ textAlign:'center', padding:60, color:'#9B8F7A', fontSize:13 }}>No site data</div>
              : <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={siteData}>
                    <XAxis dataKey="name" tick={{ fontSize:10, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                    <YAxis tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                    <Tooltip {...TT}/>
                    <Bar dataKey="value" fill="#C9A84C" radius={[4,4,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
            }
          </div>
        </div>
      </>)}

      {/* INCIDENTS TAB */}
      {tab==='incidents' && (<>
        <div style={S.grid2}>
          <div style={S.card}>
            <div style={S.title}>Daily Incident Trend</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={trendData}>
                <XAxis dataKey="date" tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip {...TT}/>
                <Line type="monotone" dataKey="incidents" stroke="#C0392B" strokeWidth={2} dot={{ fill:'#C0392B', r:4 }}/>
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div style={S.card}>
            <div style={S.title}>Top Categories</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={categoryData.slice(0,5)}>
                <XAxis dataKey="name" tick={{ fontSize:10, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip {...TT}/>
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {categoryData.slice(0,5).map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Incidents table */}
        <div style={S.card}>
          <div style={S.title}>Recent Incidents</div>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead><tr style={{ background:'#F8F7F4' }}>
              {['Site','Category','Level','Officer','Date','Status'].map(h=>(
                <th key={h} style={{ fontSize:10, color:'#9B8F7A', letterSpacing:1.5, padding:'10px 12px', borderBottom:'1px solid #EAE6DE', textAlign:'left', fontWeight:700 }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {incidents.slice(0,10).map(i=>(
                <tr key={i.id} style={{ borderBottom:'1px solid #F4F0E8' }}>
                  <td style={{ padding:'10px 12px', fontSize:12 }}>{i.siteName||'—'}</td>
                  <td style={{ padding:'10px 12px', fontSize:12 }}>{i.category||'—'}</td>
                  <td style={{ padding:'10px 12px' }}>
                    <span style={{ background:(i.escalationLevel||'').includes('3')?'#FEF2F2':(i.escalationLevel||'').includes('2')?'#FFF8E7':'#F0FFF4', color:(i.escalationLevel||'').includes('3')?'#C0392B':(i.escalationLevel||'').includes('2')?'#C9A84C':'#2E7D52', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700 }}>{i.escalationLevel||'L1'}</span>
                  </td>
                  <td style={{ padding:'10px 12px', fontSize:12 }}>{i.guardName||'—'}</td>
                  <td style={{ padding:'10px 12px', fontSize:11, color:'#9B8F7A' }}>{i.createdAt?.toDate?i.createdAt.toDate().toLocaleDateString('en-GB'):''}</td>
                  <td style={{ padding:'10px 12px' }}>
                    <span style={{ background:i.status==='open'?'#FEF2F2':'#F0FFF4', color:i.status==='open'?'#C0392B':'#2E7D52', padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, textTransform:'capitalize' }}>{i.status||'open'}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>)}

      {/* PATROLS TAB */}
      {tab==='patrols' && (
        <div style={S.grid2}>
          <div style={S.card}>
            <div style={S.title}>Patrol Completion Rate</div>
            <div style={{ textAlign:'center', padding:'20px 0' }}>
              <div style={{ fontSize:64, fontWeight:800, color: patrolRate>=80?'#2E7D52':patrolRate>=50?'#C9A84C':'#C0392B' }}>{patrolRate}%</div>
              <div style={{ fontSize:13, color:'#9B8F7A', marginTop:8 }}>{patrols.filter(p=>p.completed).length} of {patrols.length} patrols completed</div>
            </div>
          </div>
          <div style={S.card}>
            <div style={S.title}>Patrol Status</div>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={[{name:'Completed',value:patrols.filter(p=>p.completed).length,color:'#2E7D52'},{name:'Incomplete',value:patrols.filter(p=>!p.completed).length,color:'#C0392B'}]} cx="50%" cy="50%" outerRadius={80} dataKey="value" label>
                  {[{color:'#2E7D52'},{color:'#C0392B'}].map((e,i)=><Cell key={i} fill={e.color}/>)}
                </Pie>
                <Tooltip {...TT}/>
                <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize:11 }}/>
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* SHIFTS TAB */}
      {tab==='shifts' && (
        <div style={S.grid2}>
          <div style={S.card}>
            <div style={S.title}>Shifts by Type</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={shiftTypeData}>
                <XAxis dataKey="name" tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false}/>
                <YAxis tick={{ fontSize:11, fill:'#9B8F7A' }} axisLine={false} tickLine={false} allowDecimals={false}/>
                <Tooltip {...TT}/>
                <Bar dataKey="value" radius={[4,4,0,0]}>
                  {shiftTypeData.map((_,i)=><Cell key={i} fill={['#C9A84C','#1A1A2E','#2E7D52','#7C3AED'][i%4]}/>)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={S.card}>
            <div style={S.title}>Shift Summary</div>
            <div style={{ padding:'20px 0' }}>
              {[['Total Shifts',shifts.length,'#1A1A2E'],['Day Shifts',shifts.filter(s=>s.type==='Day').length,'#C9A84C'],['Night Shifts',shifts.filter(s=>s.type==='Night').length,'#1A1A2E'],['AM Shifts',shifts.filter(s=>s.type==='AM').length,'#2E7D52'],['PM Shifts',shifts.filter(s=>s.type==='PM').length,'#7C3AED']].map(([l,v,c])=>(
                <div key={l} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'10px 0', borderBottom:'1px solid #F4F0E8' }}>
                  <span style={{ fontSize:13, color:'#1A1A2E' }}>{l}</span>
                  <span style={{ fontSize:16, fontWeight:800, color:c }}>{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
